import { HELP_VIDEOS, type HelpVideo } from "./helpContent";

/**
 * Noticeboard Fundamentals: the whole course as data, shaped like the website's
 * blog posts. Edit a lesson here; the course pages only lay it out.
 */

export const COURSE_PATH = "/help/courses";

/** How many lessons the course will have once every one is written. */
export const COURSE_LENGTH = 20;

export const COURSE_MODULES = ["Getting set up", "Sending", "Going further"] as const;
export type CourseModule = (typeof COURSE_MODULES)[number];

/** A heading with paragraphs, numbered steps (in the screen's own labels), or both. */
export type LessonSection = { heading: string; paragraphs?: string[]; steps?: string[] };

/** A Help Center video by id, or a screenshot in /public. */
export type LessonBanner = { video: string } | { image: string; alt: string };

export type Lesson = {
  slug: string;
  number: number;
  module: CourseModule;
  title: string;
  intro: string;
  /** One sentence: the point, for someone who reads nothing else. */
  idea: string;
  sections: LessonSection[];
  banner: LessonBanner;
  /** Steps a Creator can't follow. Still shown to everyone. */
  forAdmins: boolean;
};

/** Only lessons that are written. The rest are added as they're drafted. */
export const LESSONS: Lesson[] = [
  {
    slug: "what-is-noticeboard",
    number: 1,
    module: "Getting set up",
    title: "What is Noticeboard?",
    intro:
      "Noticeboard is where your Organization sends its official news to families, and where they can always find it again.",
    idea: "You write a Notice here on the dashboard, choose who it's for, and it reaches their phones in the Noticeboard app.",
    sections: [
      {
        heading: "Two sides of Noticeboard",
        paragraphs: [
          "You're on the dashboard, the website the Team uses. Families use the Noticeboard app on their phones. Whatever you send from here shows up there, and stays there to find again.",
        ],
      },
      {
        heading: "The words you'll see",
        paragraphs: [
          "Notice: anything you send, such as Information, an Event, an Image or a Poll.",
          "Circle: a group inside your Organization, such as Grade 2 or the choir. Everyone means the whole Organization.",
          "User: someone who gets your Notices in the app, usually a parent.",
          "Team member: someone who uses the dashboard, like you. Each is an Admin, an Owner or a Creator.",
        ],
      },
      {
        heading: "How this course works",
        paragraphs: [
          "Getting set up covers Circles, Users and the Team. Sending covers every kind of Notice. Sending also covers personalised Celebrations. Going further covers Signatures, Certificates, the Library and Ask Noticeboard. Lessons marked For admins use pages a Creator can't open.",
        ],
      },
    ],
    banner: { video: "why-noticeboard" },
    forAdmins: false,
  },
  {
    slug: "finding-your-way-around",
    number: 2,
    module: "Getting set up",
    title: "Finding your way around",
    intro: "Everything in the dashboard is a click away from the sidebar on the left.",
    idea: "The sidebar takes you to every part of Noticeboard, and Home shows what's going on at a glance.",
    sections: [
      {
        heading: "Your everyday pages",
        paragraphs: [
          "The sidebar on the left is your way around the dashboard. On a phone, you'll find the same pages in the menu. Some pages are only shown when your role and Organization give you access.",
          "At the top is Home, where you can see what's happening and start a Notice quickly. Just below it, Create lets you choose what to send: Information, an Event, an Image or a Poll.",
          "Once you've started sending, Notices is where you'll find your sent Notices, scheduled Notices and Drafts. Calendar comes next, showing your Events by date.",
        ],
      },
      {
        heading: "People, files and help",
        paragraphs: [
          "Next come the pages for looking after your Organization. Users is for the people who receive your Notices, usually parents. Circles groups them into audiences, such as Grade 2 or the choir. Team is for the people who use this dashboard, with roles that decide what they can do.",
          "Below those, Library gives you ready-made Worksheets and Resources. Drive holds the files your Organization shares with its Circles. If you need help using a page, Help Center has tutorials and this course.",
          "Ad Center follows for Organizations that use Advertisements. At the bottom, Account brings together your Organization's details, its Plan, Reset Password and Logout.",
        ],
      },
      {
        heading: "A shortcut when you need a hand",
        paragraphs: [
          "You don't always have to look through the sidebar. Press the sparkle next to your picture to open Ask Noticeboard. Ask it to find a page, answer a question about your Organization, or help prepare a Notice. You still review the Notice before sending it.",
        ],
      },
    ],
    banner: {
      image: "/videos/course-home.webp",
      alt: "The Home page of the dashboard, with the sidebar on the left and the calendar on the right",
    },
    forAdmins: false,
  },
  {
    slug: "creating-a-circle",
    number: 3,
    module: "Getting set up",
    title: "Creating a Circle",
    intro: "A Circle is a group of Users in your Organization, such as the parents of Grade 2 or the families in the choir.",
    idea: "Circles are groups you can send Notices to. Choose a Circle when the Notice is for that group, or Everyone when it is for the whole Organization.",
    sections: [
      {
        heading: "Think of Circles as groups",
        paragraphs: [
          "Start with the groups your Organization already has. At a school, you might have Circles called Grade 2, Grade 2A, Choir or Under-11 Soccer. In a church, you might have Sunday School or Youth.",
          "Each Circle contains the Users who need news about that group. For example, a Grade 2 Circle would contain the parents of Grade 2 children. A Notice about their class outing can go to Grade 2, while a choir rehearsal reminder can go to Choir.",
          "A User can belong to more than one Circle. A parent with a child in Grade 2 and another in Grade 5 could be in both, and in Choir too. You don't need a separate User for each group.",
          "Choose names people will recognise, and check your Plan on Account for how many Circles you can create.",
        ],
      },
      {
        heading: "Create a Circle",
        steps: [
          "Go to Circles in the sidebar.",
          "Press CREATE CIRCLE at the top, or Add Your First Circle if you have none yet.",
          "Fill in Circle Name, such as Grade 2, and Circle Description, such as News for Grade 2 families. Choose 1 to 3 Learning points.",
          "Press Add logo if you'd like a picture for it.",
          "Press Create Sub-Circle.",
        ],
      },
      {
        heading: "Who's in it",
        paragraphs: [
          "With Public Circle off, only people you invite can join. Turn it on and anyone in your Organization can join from the app.",
          "Under Add Members, press + next to each User who belongs in the group. For Grade 2, add the parents of Grade 2 children. You can add people later too.",
        ],
      },
    ],
    banner: { video: "create-circle" },
    forAdmins: true,
  },
  {
    slug: "inviting-users",
    number: 4,
    module: "Getting set up",
    title: "Inviting Users, and handling Join requests",
    intro: "Users are the people who get your Notices, usually parents. Here's how to bring them in.",
    idea: "Invite Users by email or phone, put them in their Circles as you go, and check REQUESTS for anyone asking to join.",
    sections: [
      {
        heading: "Invite Users",
        steps: [
          "Go to Users in the sidebar and press ADD USER.",
          "Choose Email, Phone or Username, then type who you're inviting. You can paste several email addresses at once.",
          "Under Add to circles (optional), tap the Circles they belong in.",
          "Press Add user. Anyone who already has the app is added straight away; anyone else waits under PENDING INVITATIONS until they sign up.",
        ],
      },
      {
        heading: "Join requests",
        paragraphs: [
          "Parents can also ask to join from your Organization's public page. Their requests appear on Users under REQUESTS.",
        ],
        steps: [
          "Open the REQUESTS tab on Users.",
          "Press Add user beside a request. Add User opens with their details filled in.",
          "Choose their Circles and press Add user. To turn a request down, press the cross instead.",
        ],
      },
    ],
    banner: { video: "add-user" },
    forAdmins: true,
  },
  {
    slug: "adding-a-team-member",
    number: 5,
    module: "Getting set up",
    title: "Adding a Team member: Admin, Owner, Creator",
    intro: "Team members are the staff who use the dashboard. Each one has a role that decides what they can do.",
    idea: "Give each Team member only the role they need: most teachers just need to be a Creator.",
    sections: [
      {
        heading: "The three roles",
        paragraphs: [
          "Creator: sends Notices to the Circles they're given. Needs at least one Circle.",
          "Owner: can also look after Circles and the Team. Needs a phone number.",
          "Admin: can do everything, including Users.",
        ],
      },
      {
        heading: "Add a Team member",
        steps: [
          "Go to Team in the sidebar and press ADD MEMBER.",
          "Type their Full name and Phone number.",
          "Under Role, choose Creator or Owner.",
          "Under Circle access, press Add circles and tick the Circles they'll send to.",
          "Press Send invite.",
        ],
      },
      {
        heading: "Help them sign in",
        paragraphs: [
          "After you press Send invite, you'll see their Login Credentials: a username and a PIN. Share these privately, along with the dashboard's web address, then press Done. Each Team member should use their own details rather than sharing your login.",
          "Team members sign in to the dashboard in a web browser. This is different from the Noticeboard app that families use to receive Notices.",
        ],
        steps: [
          "Open the dashboard's web address and go to Login.",
          "Under Login as, choose Team Member, even if your Team role is Admin or Owner.",
          "In Username or Phone Number, enter the username provided in Login Credentials, or your registered phone number.",
          "Enter your 4-digit PIN and press LOGIN.",
        ],
      },
      {
        heading: "If they need help later",
        paragraphs: [
          "If a Team member forgets their PIN, Forgot PIN? on Login tells them to contact their Organization administrator or Noticeboard support for a reset. They shouldn't create another account.",
          "To change someone's role or Circles, press the pencil beside them on Team.",
        ],
      },
    ],
    banner: {
      image: "/videos/course-add-team-member.webp",
      alt: "The Invite team members window, with Creator and Owner to choose from under Role",
    },
    forAdmins: true,
  },
  {
    slug: "sending-your-first-notice",
    number: 6,
    module: "Sending",
    title: "Sending your first Notice",
    intro: "A Notice is the simplest thing you can send: a title and a message, for the people who need it.",
    idea: "Give it a clear title, write the message, choose who it's for on Review, and press Publish.",
    sections: [
      {
        heading: "Write it",
        steps: [
          "Go to Create in the sidebar and press Create a notice on the Notices card.",
          "Type a short title, such as Sports day on Friday, and press Write message.",
          "Write your message and press Continue.",
        ],
      },
      {
        heading: "Choose when, and who it's for",
        steps: [
          "On Scheduling, leave Post now chosen and press Continue.",
          "On Review, under Who should receive this?, tap your Organization to send to Everyone, or tap a Circle. Only Admins can send to Everyone.",
          "Check the Live preview and press Publish.",
        ],
      },
      {
        heading: "Once it's sent",
        paragraphs: [
          "It goes straight to the app and is listed on Notices under Sent. A sent Notice can't be edited: press Duplicate to send a corrected one.",
        ],
      },
    ],
    banner: { video: "create-notice" },
    forAdmins: false,
  },
  {
    slug: "image-notices-and-attachments",
    number: 7,
    module: "Sending",
    title: "Image notices and attachments",
    intro: "Some news is best shown: a poster, a photo from the class trip, a form to print.",
    idea: "Use Image when the picture is the Notice, and Attachments when a photo, document or video goes along with it.",
    sections: [
      {
        heading: "Send an Image notice",
        steps: [
          "Go to Create and press Create a notice on the Image card.",
          "Type a title. The message underneath is optional.",
          "Click Add an image, or drag a picture onto it.",
          "Press Continue, choose when and who it's for, and press Publish.",
        ],
      },
      {
        heading: "Send attachments",
        steps: [
          "Press Create a notice on the Attachments card.",
          "Type a title, then press the round + beside it. A plain Notice has the same +, so any Notice can carry a file.",
          "Choose Photos, Library, Camera, Document or Video, and pick your file.",
          "Press Continue and finish on Review.",
        ],
      },
    ],
    banner: {
      image: "/videos/course-attachments.webp",
      alt: "An Attachment notice with its attachment menu open: Photos, Library, Camera, Document and Video",
    },
    forAdmins: false,
  },
  {
    slug: "urgent-scheduled-and-draft-notices",
    number: 8,
    module: "Sending",
    title: "Urgent, scheduled and draft Notices",
    intro: "Three settings change how and when a Notice arrives: Urgent, Schedule for later and Drafts.",
    idea: "Keep Urgent for what can't wait, schedule the rest for the right moment, and save a Draft whenever you're not ready.",
    sections: [
      {
        heading: "Urgent",
        paragraphs: [
          "On the first step, turn on Mark as urgent notice. Parents are notified by email and push notification, so keep it for things like a closure.",
        ],
      },
      {
        heading: "Schedule for later",
        steps: [
          "On Scheduling, choose Schedule for later.",
          "Pick a Publish date and a Publish time.",
          "Press Continue, then Schedule on Review.",
        ],
      },
      {
        heading: "Changing a scheduled Notice",
        paragraphs: [
          "It waits on Notices under Scheduled, where Edit changes it, Publish now sends it straight away and Cancel schedule removes it.",
        ],
      },
      {
        heading: "Drafts",
        paragraphs: [
          "Close the wizard with the cross before you publish, and press Save Draft. Your Drafts are under Pick up where you left off, at the top of Create and Notices.",
        ],
      },
    ],
    banner: {
      image: "/videos/course-schedule-notice.webp",
      alt: "The Scheduling step with Schedule for later chosen and a publish date and time filled in",
    },
    forAdmins: false,
  },
  {
    slug: "sending-an-event",
    number: 9,
    module: "Sending",
    title: "Sending an Event, with RSVPs",
    intro: "An Event is a Notice with a day, a time and a place. It goes on the Calendar, and families can tell you whether they're coming.",
    idea: "Fill in when and where, turn on RSVPs if you need numbers, and let the reminders do the chasing.",
    sections: [
      {
        heading: "Create the Event",
        steps: [
          "Go to Create and press Create an event on the Events card.",
          "Type the title and the message, pressing Continue after each.",
          "On Details, fill in the Start date, Start time and Location, and an End date and End time if you know them.",
          "Turn on RSVPs if you need to know who's coming. You can set an RSVP deadline.",
        ],
      },
      {
        heading: "Reminders and repeats",
        paragraphs: [
          "On Settings, the Reminder card chooses when the app notifies people before the Event. Repeat makes it happen Daily, Weekly, Every 2 weeks or Monthly, until an End date.",
          "Repeating Events need an End date that includes at least one more occurrence. For example, a Weekly Event must end at least one week after its Start date.",
          "On Review, choose who it's for and press Publish. It appears on the Calendar once it goes out.",
        ],
      },
    ],
    banner: { video: "create-event" },
    forAdmins: false,
  },
  {
    slug: "the-calendar-and-staff-only-events",
    number: 10,
    module: "Sending",
    title: "The Calendar and staff-only Events",
    intro: "The Calendar shows everything coming up, a month at a time.",
    idea: "Events and Scheduled Notices go on the Calendar by themselves, and an Internal team only Event is seen by your Team and nobody else.",
    sections: [
      {
        heading: "What's on it",
        paragraphs: [
          "Every Event appears on its date, and every Scheduled Notice on the day it goes out. Upcoming Activity on the right lists what's next.",
        ],
      },
      {
        heading: "Events from the Calendar",
        steps: [
          "Press ADD EVENT, or click a date to start an Event on that day.",
          "Fill it in as in lesson 9 and press Publish.",
          "Click an Event to see its details, and View notice to open it.",
        ],
      },
      {
        heading: "Internal team only Events",
        paragraphs: [
          "On an Event's Review step, choose Internal team only under Who should receive this?. Only your Team sees it, so it suits staff meetings and planning days.",
        ],
      },
    ],
    banner: {
      image: "/videos/course-calendar.webp",
      alt: "The Calendar showing a month of Events, with Upcoming Activity on the right",
    },
    forAdmins: false,
  },
  {
    slug: "sending-a-poll",
    number: 11,
    module: "Sending",
    title: "Sending a Poll",
    intro: "A Poll asks families a question and lets them answer with one tap in the app.",
    idea: "Ask one clear question, give the choices, choose who it's for and press Publish; the answers come back to you as they vote.",
    sections: [
      {
        heading: "Write the Poll",
        steps: [
          "Go to Create and press Create a poll on the Poll card.",
          "Under QUESTION, type what you'd like to ask.",
          "Under OPTIONS, type the choices. Press ADD OPTION for another one.",
          "Press Continue.",
        ],
      },
      {
        heading: "Send it",
        steps: [
          "On Review, under Who should receive this?, tap your Organization for Everyone, or tap a Circle.",
          "Press Publish. A Poll goes out straight away; it can't be scheduled.",
        ],
      },
    ],
    banner: {
      image: "/videos/course-poll.webp",
      alt: "The Poll wizard with a question and two options filled in",
    },
    forAdmins: false,
  },
  {
    slug: "sending-a-claim-list",
    number: 12,
    module: "Sending",
    title: "Sending a Claim list",
    intro: "A Claim list is a sign-up sheet in the app: each item can be taken once, by one person.",
    idea: "List what you need, and each family claims one item; once something is taken it disappears for everyone else.",
    sections: [
      {
        heading: "When to use one",
        paragraphs: [
          "Use a Claim list when two people shouldn't pick the same thing: who brings what to the bake sale, or interview slots. If people can pick the same answer, send a Poll.",
        ],
      },
      {
        heading: "Create a Claim list",
        steps: [
          "Go to Create and press Create a claim list on the Claims card.",
          "Under TITLE, say what can be claimed, such as Bake sale: what can you bring?",
          "Under OPTIONS, add one item per line. Press ADD OPTION for more.",
          "Press Continue, choose who it's for on Review, and press Publish.",
        ],
      },
      {
        heading: "Seeing who claimed what",
        paragraphs: [
          "Open the Notice from Notices. Under Poll Results, each taken item says Claimed by and the person's name.",
        ],
      },
    ],
    banner: {
      image: "/videos/course-claim-list.webp",
      alt: "The Claims wizard with a title and two items to claim filled in",
    },
    forAdmins: false,
  },
  {
    slug: "saving-time-with-templates",
    number: 13,
    module: "Sending",
    title: "Saving time with Templates",
    intro: "A Template saves the starting text and settings for a Notice you send often. Use it again, update the details, and send a new Notice.",
    idea: "Keep what stays the same in a Template, and change what's different each time you use it.",
    sections: [
      {
        heading: "What a Template does",
        paragraphs: [
          "Think of a school photograph Notice: the instructions may stay the same each year, while the date and price change. A Template gives you that starting point without writing it all again.",
          "A Template isn't a sent Notice or a Draft. Using one starts a new Notice for you to finish. Changing that Notice doesn't change the saved Template, and opening a Template sends nothing to families.",
        ],
      },
      {
        heading: "Find and use a Template",
        paragraphs: [
          "On Create, scroll to Explore Templates. You'll find ready-made choices such as Aftercare and School photographs alongside your Organization's saved Templates. Use All, System, Information or Event to narrow the list.",
        ],
        steps: [
          "Click the Template you want to use.",
          "If Fill in the values appears, enter the details for this Notice. These Configurable values are the parts that change, such as the photograph date and amount.",
          "Read the filled-in title and message, and change anything else you need.",
          "Check when it should go out and who should receive it, then review the Notice and press Publish.",
        ],
      },
      {
        heading: "Save a Template your Team can reuse",
        paragraphs: [
          "Admins and Owners can create Templates. For example, save a Weekly newsletter with your usual greeting and headings, then fill in that week's news whenever you use it. Creators can use Templates without creating their own.",
        ],
        steps: [
          "On Create, press Create template on the Templates card.",
          "Give it a Template name you'll recognise, such as Weekly newsletter. This helps your Team find it; Notice title is the starting title families will see.",
          "Choose Information or Event under Notice type, then fill in Notice title and Description with the text you want to reuse.",
          "Choose any other settings you need, such as Audience (optional) and a background, and press Save. Saving the Template does not publish a Notice.",
        ],
      },
    ],
    banner: {
      image: "/videos/course-templates.webp",
      alt: "Explore Templates on Create, with ready-made Templates such as Aftercare and School photographs",
    },
    forAdmins: false,
  },
  {
    slug: "sending-celebrations",
    number: 14,
    module: "Sending",
    title: "Sending Celebrations",
    intro: "Make a personal card for a birthday, anniversary, welcome, farewell or graduation, and share it with the Circle celebrating together.",
    idea: "Choose an occasion and a template, personalise the wording and photo, then review who receives it and when.",
    sections: [
      {
        heading: "Choose the occasion and design",
        steps: [
          "Go to Create and open Celebrations.",
          "At the top of Celebration details, choose Birthday, Anniversary, Welcome, Farewell or Graduation under Occasion.",
          "Scroll through the Template carousel, or press View all to compare the designs and select one.",
        ],
      },
      {
        heading: "Make it personal",
        steps: [
          "Enter Name on card, then choose or edit the Heading and write your Message. For occasions with a Small label, you can edit that too.",
          "Click the photo area to add a picture. Click it again to crop or reposition it; Change photo is inside the crop window. Press Save when it looks right.",
          "Check the card preview, including longer names and messages. The text-only graduation design does not need a photo.",
        ],
      },
      {
        heading: "Review and schedule",
        steps: [
          "Press Review and schedule.",
          "Choose the Circle that should receive the card, or Everyone if your role allows it.",
          "Choose to send now or schedule it for the right date and time, then check the preview and confirm.",
        ],
        paragraphs: [
          "The finished card reaches families as an Image notice. Use Certificates when you want to recognise an achievement with an Award; use Celebrations to mark a personal milestone or welcome or farewell someone.",
        ],
      },
    ],
    banner: {
      image: "/birthdays/welcome-teal.webp",
      alt: "Teal and gold artwork from the Celebrations welcome card collection",
    },
    forAdmins: false,
  },
  {
    slug: "after-its-sent",
    number: 15,
    module: "Sending",
    title: "Checking Notices and sending Reminders",
    intro: "Once a Notice is out, Notices shows you how it's doing.",
    idea: "Open a sent Notice to see who has read it and how people answered, and use Duplicate when something needs to change.",
    sections: [
      {
        heading: "Who's seen it",
        paragraphs: [
          "On Notices, under Sent, VIEWS shows how many people have seen each Notice. Open the Notice and click Viewers to see their names.",
        ],
      },
      {
        heading: "Poll results and RSVPs",
        paragraphs: [
          "A Poll opens on Poll Results, with the votes for each choice and Who Voted. An Event with RSVPs shows Who responded.",
        ],
      },
      {
        heading: "Changing it",
        paragraphs: [
          "A sent Notice can't be edited. Press Duplicate to copy it, fix it and publish again.",
          "A Scheduled Notice can still be changed: press Edit on it under Scheduled.",
          "For 30 minutes after you send a Notice, Remove takes it down.",
        ],
      },
      {
        heading: "Send a Reminder",
        paragraphs: [
          "Check VIEWS first. If most people have seen the Notice, a Reminder may not be needed. Send reminder publishes a new Notice to the same audience.",
          "On a Poll's page, Not Responded lists who hasn't answered. Press the paper plane beside a name to remind just that person.",
        ],
        steps: [
          "On Notices, under Sent, find the Notice and press Send reminder.",
          "Check the new title, which starts with Reminder -, and press Send reminder to confirm.",
        ],
      },
    ],
    banner: {
      image: "/videos/course-after-its-sent.webp",
      alt: "A sent Poll's page, with Poll Results, Participation and Send Reminder",
    },
    forAdmins: false,
  },
  {
    slug: "adding-signatures",
    number: 16,
    module: "Going further",
    title: "Adding Signatures",
    intro: "A Signature is someone who signs your certificates, like your Principal. Add them once and every Award can use them.",
    idea: "Add the people who sign your certificates on Signatures, then each Award picks who signs it.",
    sections: [
      {
        heading: "Add a Signature",
        steps: [
          "Go to Create and press Add signatures on the Signatures card.",
          "Press Add signature.",
          "Under Name, type the name as it should be signed.",
          "Under Title, type their role, or pick one from the list.",
          "Press Save. The name is written in a handwriting font, so nobody needs to upload a real signature.",
        ],
      },
      {
        heading: "Changing or deleting one",
        paragraphs: [
          "Press the pencil on a Signature to change it, or the bin to delete it. Awards you've already made keep the Signature they had.",
        ],
      },
    ],
    banner: {
      image: "/videos/course-signatures.webp",
      alt: "The Add signature window, with a name and the title Principal filled in and shown in handwriting above",
    },
    forAdmins: false,
  },
  {
    slug: "designing-a-certificate-template",
    number: 17,
    module: "Going further",
    title: "Designing a Certificate template",
    intro: "A Certificate template is the design every certificate in an Award is printed on: its style, colors, font and logo.",
    idea: "Use a ready-made Certificate template or design your own, and Publish it so Awards can use it.",
    sections: [
      {
        heading: "Ready-made ones",
        paragraphs: [
          "Go to Create, press Create certificates on the Certificates card, then Open Templates. Your Organization already has a ready-made template in every style, ready to use on an Award.",
        ],
      },
      {
        heading: "Design your own",
        steps: [
          "On Templates, press New certificate template.",
          "Type a Certificate template name.",
          "Under Design, choose a style, colours and a Font.",
          "Under Organisation, check the name and add a Logo.",
          "Watch the preview on the right, and press Publish when it's ready.",
        ],
      },
      {
        heading: "Publish it",
        paragraphs: [
          "Only published templates can be used on an Award. Publishing locks the design; to change it, open it and press Duplicate.",
        ],
      },
    ],
    banner: {
      image: "/videos/course-certificate-template.webp",
      alt: "New certificate template, with the Design choices on the left and a preview of the certificate on the right",
    },
    forAdmins: false,
  },
  {
    slug: "sending-certificates-with-an-award",
    number: 18,
    module: "Going further",
    title: "Sending certificates with an Award",
    intro: "An Award gives the same certificate to a list of Awardees in one go, such as everyone who got Gold in the Maths Olympics.",
    idea: "Make an Award, add each Awardee with their Users, and press Send to all.",
    sections: [
      {
        heading: "Create the Award",
        steps: [
          "Go to Create, press Create certificates on the Certificates card, then Open Awards.",
          "Press New Award.",
          "Type the Award title and choose a Certificate template.",
          "Fill in the Certificate title, Achievement name and Completion date, and choose a Signature.",
          "Type an ID prefix, such as MATH, and press Save and add Awardees.",
        ],
      },
      {
        heading: "Add the Awardees",
        paragraphs: [
          "On the Awardees tab, type each Awardee's First name and Surname, choose their Users (at a school, the child's parents), and press Add Awardee.",
        ],
      },
      {
        heading: "Send the certificates",
        paragraphs: [
          "Press Send to all. Each certificate goes to that Awardee's Users as a Notice in the app. Beside each Awardee you can preview, download a PNG or PDF, or Resend.",
        ],
      },
    ],
    banner: {
      image: "/videos/course-award.webp",
      alt: "An Award's Awardees tab, listing each Awardee with their Users, certificate ID and the date it was sent",
    },
    forAdmins: false,
  },
  {
    slug: "the-library-and-drive",
    number: 19,
    module: "Going further",
    title: "The Library and Drive",
    intro: "Library is what Noticeboard gives you, ready to use. Drive is where your Organization's own files are kept.",
    idea: "Use Library for ready-made content, and organise your own Drive files into named folders that are easy to find and keep up to date.",
    sections: [
      {
        heading: "Library",
        steps: [
          "Go to Library in the sidebar.",
          "Press Browse on a grade, then on a kind of worksheet, such as Math.",
          "Press Print on a worksheet, or Send to share it with a Circle.",
        ],
      },
      {
        heading: "Give your Drive files a home",
        paragraphs: [
          "A folder keeps related files together. Use names such as Grade 2 — Term 1, Sports fixtures or School forms, so your Team and families know where to look.",
          "The root directory is Drive's main file list, outside any folder. Leaving everything there makes that list harder to browse as files build up. Named folders are easier to find, share and review when it's time to remove old material.",
        ],
      },
      {
        heading: "Create and share a folder",
        steps: [
          "Go to Drive and press UPLOAD FOLDER.",
          "Under Drive folder, enter a clear name, such as Grade 2 — Term 1, and drag in the files that belong together.",
          "Under Who should receive this?, choose the Circle that needs them.",
          "Press Upload & Share. Families get a Notice starting with Drive folder:, so they can open the files in the app.",
        ],
      },
      {
        heading: "Keep Drive tidy",
        paragraphs: [
          "Each school has a total file-storage limit of 20 GB. That's the school's shared allowance, not a 20 GB limit for each file or folder.",
          "Review your folders regularly, especially at the end of a term. Remove duplicates, outdated forms and files nobody needs anymore. Keep anything your Organization still needs, and save a copy elsewhere before removing a file you may need later.",
          "To remove a file, press its bin icon and check the confirmation before pressing Remove file. Make sure families and your Team no longer need access to it.",
        ],
      },
    ],
    banner: {
      image: "/videos/course-drive.webp",
      alt: "Drive, with All Circles at the top, then the Folders and the Files shared with your Circles",
    },
    forAdmins: false,
  },
  {
    slug: "ask-noticeboard",
    number: 20,
    module: "Going further",
    title: "Ask Noticeboard",
    intro: "Ask Noticeboard is a helper built into the dashboard. Ask it a question in your own words, or ask it to put a Notice together for you.",
    idea: "Ask in plain words, answer its questions, and check what it fills in: nothing is published until you press Publish.",
    sections: [
      {
        heading: "Open it",
        paragraphs: [
          "Press the sparkle next to your picture, or ⌘K on a Mac or Ctrl+K on Windows. On a phone, the sparkle is at the top right.",
        ],
      },
      {
        heading: "Ask a question",
        paragraphs: [
          "Try one of the examples, or type your own, such as How many people viewed my last notice?. Type a page name, such as Calendar, to go straight there.",
        ],
      },
      {
        heading: "Let it start a Notice",
        steps: [
          "Ask for what you need, such as create a notice, add an event or create a poll.",
          "Answer its questions. To change something, say so, or say cancel to stop.",
          "Check the summary and press Looks good. Create notice opens with everything filled in.",
          "Check it and press Publish.",
        ],
      },
    ],
    banner: {
      image: "/videos/course-ask-noticeboard.webp",
      alt: "Ask Noticeboard open, asking What can I help you find? with example questions to try",
    },
    forAdmins: false,
  },
];

const WORDS_PER_MINUTE = 225;

/** Words a reader gets through, counted the way the website blog counts them. */
function wordCount(lesson: Lesson): number {
  return [
    lesson.intro,
    lesson.idea,
    ...lesson.sections.flatMap((s) => [s.heading, ...(s.paragraphs ?? []), ...(s.steps ?? [])]),
  ]
    .join(" ")
    .trim()
    .split(/\s+/).length;
}

/** "N min read": word count over the blog's reading speed, rounded up. */
export const readMinutes = (lesson: Lesson) =>
  Math.max(1, Math.ceil(wordCount(lesson) / WORDS_PER_MINUTE));

export const lessonPath = (lesson: Lesson) => `${COURSE_PATH}/${lesson.slug}`;

export const lessonBySlug = (slug: string | undefined) =>
  LESSONS.find((l) => l.slug === (slug === "sending-a-reminder" ? "after-its-sent" : slug));

export const previousLesson = (lesson: Lesson): Lesson | undefined =>
  LESSONS[LESSONS.indexOf(lesson) - 1];

/** The next written lesson; none after the last, where Next goes to the course home. */
export const nextLesson = (lesson: Lesson): Lesson | undefined =>
  LESSONS[LESSONS.indexOf(lesson) + 1];

/** The modules that have a written lesson, each with its lessons in order. */
export const writtenModules = () =>
  COURSE_MODULES.map((module) => ({
    module,
    lessons: LESSONS.filter((l) => l.module === module),
  })).filter((m) => m.lessons.length > 0);

export const bannerVideo = ({ banner }: Lesson): HelpVideo | undefined =>
  "video" in banner ? HELP_VIDEOS.find((v) => v.id === banner.video) : undefined;

/** The course home or one of its lessons: Help Center stays highlighted and the page is full-bleed. */
export const isCoursePath = (pathname: string) =>
  pathname === COURSE_PATH || pathname.startsWith(`${COURSE_PATH}/`);
